import { Fragment } from 'react';
import { Link } from '@backstage/core-components';
import {
  CustomHomepageGrid,
  HomePageRecentlyVisited,
  HomePageStarredEntities,
  HomePageTopVisited,
} from '@backstage/plugin-home';
import {
  Box,
  Container,
  Flex,
  FullPage,
  PluginHeader,
  Text,
} from '@backstage/ui';
import { Grid, useTheme } from '@material-ui/core';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import { RiInboxFill } from '@remixicon/react';
import {
  AnnouncementList,
  BackstageLogoIcon,
  CopilotBudget,
  PluginOrTemplateDecisionGuideWidget,
  EmptyWidget,
  FeaturedTechDocs,
  NotificationListWidget,
  Page,
  QuickLinks,
  BackstageProfileLink,
} from '@internal/backstage-plugin-hm-react';
import type { HomePageWidgetData } from '@backstage/plugin-home-react/alpha';
// @NFS: need to find a way to avoid this import from frontend plugin
import { TechSpotEventCalendarWidget } from '@internal/backstage-plugin-techspot-event-calendar';
import { PulseInsightsCard } from '@internal/backstage-plugin-pulse';

const defaultConfig = [
  {
    component: <EmptyWidget />,
    x: 0,
    y: 0,
    width: 12,
    height: 1,
  },
];

const SectionDivider = () => {
  return (
    <Divider
      orientation="horizontal"
      style={{
        height: '2px',
        margin: 'var(--bui-space) 0 var(--bui-space-4)',
        opacity: 0.5,
      }}
    />
  );
};

const SectionHeadline = ({
  title,
  action,
}: {
  title: string;
  action: { href: string; label: string };
}) => {
  return (
    <>
      <Flex direction="row" align="center" justify="between" gap="2">
        <Text as="h2" variant="title-x-small">
          {title}
        </Text>
        <IconButton
          component={Link}
          to={action.href}
          size="small"
          title={action.label}
        >
          <RiInboxFill />
        </IconButton>
      </Flex>
      <SectionDivider />
    </>
  );
};

export const HomeLandingPage = ({
  widgets,
}: {
  widgets: HomePageWidgetData[];
}) => {
  const theme = useTheme();
  const iconColor =
    theme.palette.type === 'dark' ? undefined : theme.palette.text.primary;

  return (
    <Page title="Welcome home!">
      <PluginHeader
        title="Welcome home!"
        icon={<BackstageLogoIcon color={iconColor} height={16} />}
        customActions={<BackstageProfileLink />}
      />
      <FullPage>
        <Container>
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <SectionHeadline
                title="What's new in the Portal"
                action={{
                  href: '/qeta/collections/1',
                  label: 'All announcements',
                }}
              />
              <AnnouncementList />
            </Grid>
            <Grid item xs={12} md={3}>
              <SectionHeadline
                title="Featured TechDocs"
                action={{ href: '/docs', label: 'All TechDocs' }}
              />
              <FeaturedTechDocs />
            </Grid>
            <Grid item xs={12} md={6}>
              <Flex direction="column" gap="8">
                <Box>
                  <Text as="h2" variant="title-x-small">
                    Usage & Compliance
                  </Text>
                  <SectionDivider />
                  <Flex direction="column" gap="4">
                    <CopilotBudget />
                    <PulseInsightsCard />
                  </Flex>
                </Box>

                <Box>
                  <Text as="h2" variant="title-x-small">
                    Discover Backstage
                  </Text>
                  <SectionDivider />
                  <Flex direction="column" gap="4">
                    <QuickLinks />
                  </Flex>
                </Box>
              </Flex>
            </Grid>
          </Grid>

          <div style={{ margin: theme.spacing(3, 0) }} />
          <CustomHomepageGrid
            config={defaultConfig}
            style={{ margin: theme.spacing(0, 0, 0, -1) }}
          >
            {widgets.map((widget, index) => (
              <Fragment key={widget.name ?? index}>{widget.component}</Fragment>
            ))}
            <EmptyWidget />
            <PluginOrTemplateDecisionGuideWidget />
            <NotificationListWidget />
            <TechSpotEventCalendarWidget />
            <HomePageTopVisited />
            <HomePageStarredEntities />
            <HomePageRecentlyVisited />
          </CustomHomepageGrid>
        </Container>
      </FullPage>
    </Page>
  );
};
